/**
 * advisor-rules.js — resolve the company rule set against one normalised plan
 * (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md, "Rules").
 *
 * Pure. The rule set comes from GET /agents/advisor-rules, which the server validated on save
 * (netlify/functions/shared/advisor-rules-model.js); this module still re-checks each rule's shape
 * so a malformed row can never hide a plan.
 *
 * Precedence: the most specific matching rule wins. A plan target beats a carrier + product type
 * target, which beats a carrier target, which beats a product type target; a state-limited rule
 * beats the same target in every state. Between equally specific rules, hide beats tier, and the
 * lower-ranked tier wins, so a conflict always resolves to the more cautious view.
 *
 * Levels: stage 1 applies company rules only. Group and agent rules (stages 3+) will layer on top
 * through LEVEL_ORDER without changing the stored shape.
 */

export const TIER_LABELS = { 1: 'Preferred', 2: 'Standard', 3: 'Last resort' };
export const DEFAULT_TIER = 2;
export const DEFAULT_FIT_FLOOR = 50;
export const DEFAULT_WEIGHTS = { price: 30, coverage: 25, suitability: 25, doctorsDrugs: 15, wants: 5 };
const LEVEL_ORDER = ['company'];

/** The rule set used when none is stored or the stored one cannot be loaded. */
export function emptyRuleSet() {
  return { version: 0, rules: [], fitFloor: DEFAULT_FIT_FLOOR, weights: { ...DEFAULT_WEIGHTS } };
}

function norm(s) {
  return String(s == null ? '' : s).trim().toLowerCase();
}

function isUsableRule(r) {
  if (!r || typeof r !== 'object' || !r.target || typeof r.target !== 'object') return false;
  if (!LEVEL_ORDER.includes(r.level || 'company')) return false;
  if (r.action !== 'hide' && !(r.action === 'tier' && [1, 2, 3].includes(Number(r.tier)))) return false;
  const t = r.target;
  return !!(t.planId || t.carrier || t.productType);
}

/** Read a rule set defensively: unusable rules are dropped, missing numbers take defaults. */
export function readRuleSet(raw) {
  const base = emptyRuleSet();
  if (!raw || typeof raw !== 'object') return base;
  const weights = { ...DEFAULT_WEIGHTS };
  if (raw.weights && typeof raw.weights === 'object') {
    Object.keys(DEFAULT_WEIGHTS).forEach((k) => {
      const w = Number(raw.weights[k]);
      if (Number.isFinite(w) && w >= 0) weights[k] = w;
    });
  }
  const floor = Number(raw.fitFloor);
  return {
    version: Number.isFinite(Number(raw.version)) ? Number(raw.version) : 0,
    rules: Array.isArray(raw.rules) ? raw.rules.filter(isUsableRule) : [],
    fitFloor: Number.isFinite(floor) && floor >= 0 && floor <= 100 ? floor : DEFAULT_FIT_FLOOR,
    weights,
  };
}

function specificity(rule) {
  const t = rule.target;
  let s;
  if (t.planId) s = 8;
  else if (t.carrier && t.productType) s = 6;
  else if (t.carrier) s = 4;
  else s = 2;
  return s + (Array.isArray(rule.states) && rule.states.length ? 1 : 0);
}

function matches(rule, plan, state) {
  const t = rule.target;
  if (Array.isArray(rule.states) && rule.states.length) {
    if (!state || !rule.states.includes(state)) return false;
  }
  if (t.planId) return String(t.planId) === String(plan.planId);
  if (t.carrier && norm(t.carrier) !== norm(plan.carrier)) return false;
  if (t.productType && t.productType !== plan.productType) return false;
  return true;
}

function caution(rule) {
  return rule.action === 'hide' ? 10 : Number(rule.tier);
}

/**
 * Resolve the effective treatment of a plan.
 * @param {Object} plan - normalised plan (advisor-normalize.js): planId, carrier, productType
 * @param {Object} ruleSet - from readRuleSet
 * @param {string|null} state - the quote's two-letter state
 * @returns {{ hidden: boolean, tier: number, rule: Object|null }}
 */
export function resolvePlanRule(plan, ruleSet, state) {
  const rules = (ruleSet && ruleSet.rules) || [];
  let best = null;
  for (const rule of rules) {
    if (!matches(rule, plan, state)) continue;
    if (!best) { best = rule; continue; }
    const a = specificity(rule);
    const b = specificity(best);
    if (a > b || (a === b && caution(rule) > caution(best))) best = rule;
  }
  if (!best) return { hidden: false, tier: DEFAULT_TIER, rule: null };
  if (best.action === 'hide') return { hidden: true, tier: null, rule: best };
  return { hidden: false, tier: Number(best.tier), rule: best };
}

/** Normalised weights (summing to 1) after the client's priority shifts them. */
export function effectiveWeights(weights, priority) {
  let w = { ...DEFAULT_WEIGHTS, ...(weights || {}) };
  // All-zero weights would score every plan 0; the server refuses them, and so does the reader.
  if (Object.values(w).every((v) => !(v > 0))) w = { ...DEFAULT_WEIGHTS };
  const shift = {
    lowest_price: { price: 15 },
    best_coverage: { coverage: 10, suitability: 5 },
    keep_doctors: { doctorsDrugs: 15 },
  }[priority];
  if (shift) Object.keys(shift).forEach((k) => { w[k] += shift[k]; });
  const total = Object.values(w).reduce((s, v) => s + v, 0) || 1;
  const out = {};
  Object.keys(w).forEach((k) => { out[k] = w[k] / total; });
  return out;
}

export const AdvisorRules = {
  TIER_LABELS, DEFAULT_TIER, DEFAULT_FIT_FLOOR, DEFAULT_WEIGHTS,
  emptyRuleSet, readRuleSet, resolvePlanRule, effectiveWeights,
};
